/**
 * ry-ui
 *
 * Framework-agnostic, Light DOM web components.
 * CSS is the source of truth.
 *
 * Usage:
 * <script type="module" src="ry-ui.js"></script>
 *
 * Transform wrapper (optional clean syntax):
 * <ry>
 *   <accordion>
 *     <accordion-item title="Foo">Content</accordion-item>
 *   </accordion>
 * </ry>
 *
 * Or use <template ry> for inert content:
 * <template ry>
 *   <accordion>...</accordion>
 * </template>
 */

// Types
export type {
  ComponentState,
  StateChangeDetail,
  ChangeDetail,
  CleanupFn,
  ToastVariant,
  ToastOptions,
  SelectOption
} from './types.js';

// Core
export { RyElement } from './core/ry-element.js';
export { transform, processTransforms } from './core/ry-transform.js';

// Components
export { RyAccordion } from './components/ry-accordion.js';
export { RyModal } from './components/ry-modal.js';
export { RyTabs } from './components/ry-tabs.js';
export { RyDropdown } from './components/ry-dropdown.js';
export { RyButton } from './components/ry-button.js';
export { RyThemeToggle } from './components/ry-theme-toggle.js';
export { RyAlert } from './components/ry-alert.js';
export { RyField } from './components/ry-field.js';
export { RySwitch } from './components/ry-switch.js';
export { RyTooltip } from './components/ry-tooltip.js';
export { RyDrawer } from './components/ry-drawer.js';
export { RyToast } from './components/ry-toast.js';
export { RySelect } from './components/ry-select.js';
export { RyCode } from './components/ry-code.js';
export { RyExample } from './components/ry-example.js';
export { RyIcon } from './components/ry-icon.js';
export { RyToggleButton } from './components/ry-toggle-button.js';
export { RySlider } from './components/ry-slider.js';
export { RyKnob } from './components/ry-knob.js';
export { RyNumberSelect } from './components/ry-number-select.js';
export { RyColorPicker } from './components/ry-color-picker.js';
export type { ColorFormat, RGB, HSL, HSV } from './components/ry-color-picker.js';
export { RyColorInput } from './components/ry-color-input.js';
export { RyGradientPicker } from './components/ry-gradient-picker.js';
export type { GradientStop, GradientType, RadialShape } from './components/ry-gradient-picker.js';
export { RyTree, RyTreeItem } from './components/ry-tree.js';
export type { TreeNode } from './components/ry-tree.js';

// Icon registry
export { getIcon, registerIcon, registerIcons, getIconNames } from './core/ry-icons.js';

// Make RyToast globally available for programmatic API
import { RyToast as _RyToast } from './components/ry-toast.js';

declare global {
  interface Window {
    RyToast: typeof _RyToast;
  }
}

window.RyToast = _RyToast;

// Log version
console.log('ry-ui loaded');
